"use client";

import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { usercontext } from "@/lib/context";
import { startTransition, useEffect, useRef, useState } from "react";
import { useOrderListing } from "@/lib/useOrderUpdates";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";

// Only ever rendered once a ticket actually arrives over Pusher — dynamic
// import keeps its antd Modal dependency out of every page's initial bundle.
const ReceivedTicketModal = dynamic(
  () => import("@/components/Ticket/ReceivedTicketModal"),
  { ssr: false },
);
export default function UserWrapper({ children }) {
  const [loading, setLoading] = useState(true);
  const [timeLoading, setTimeLoading] = useState(false);
  const [error, setError] = useState(false);
  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [countryCode, setCountryCode] = useState();
  const [isoCode, setISOCode] = useState();
  const [timeZone, setTimeZone] = useState();
  const [specialFn, setSpecialFn] = useState(null);
  const [receivedTicket, setReceivedTicket] = useState(null);
  const router = useRouter();

  const { data: pusherData } = useOrderListing(user?.id);

  useEffect(() => {
    if (pusherData) {
      setReceivedTicket(pusherData);
    }
  }, [pusherData]);

  // Runs on mount, while the page may still be hydrating: context updates
  // here are transitions for the same reason as in fetchUser below.
  async function getMyGeoLocation() {
    try {
      startTransition(() => setTimeLoading(true));
      const res = await fetch("https://ipapi.co/json/");
      const response = await res.json();

      startTransition(() => {
        setCountryCode(response.country_calling_code || "91");
        setISOCode(response.country || "IN");
        setTimeZone(response.timezone || "Asia/Kolkata");
      });
      localStorage.setItem(
        "country",
        response.country.replace("+", "") || "91",
      );
      localStorage.setItem(
        "country_code",
        response.country_calling_code || "IN",
      );
      localStorage.setItem("timezone", response.timezone || "Asia/Kolkata");
    } catch (error) {
      startTransition(() => setError(false));
    } finally {
      startTransition(() => setTimeLoading(false));
    }
  }
  const fetchCalledRef = useRef(false);

  async function fetchUser() {
    // Guard against concurrent/duplicate calls — only one in-flight at a time.
    if (fetchCalledRef.current) return;
    fetchCalledRef.current = true;
    // Session restore runs during page load, while lazy page sections may
    // still be hydrating. Urgent context updates then would make React throw
    // away their server HTML and client-render them (a layout shift); as
    // transitions React waits for hydration first.
    try {
      startTransition(() => setLoading(true));
      const res = await fetch(
        process.env.NEXT_PUBLIC_API_URL + "/" + ENDPOINTS.ME,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            ...DEV_AUTH_HEADERS,
          },
        },
      );

      const response = await res.json();
      // customer-token is HttpOnly — never gate on document.cookie. /me with
      // credentials:include is the source of truth for session restore.
      startTransition(() => {
        if (res.ok && response?.status && response?.data?.id) {
          setUser(response.data);
        } else {
          setUser(null);
        }
      });
    } catch (error) {
      startTransition(() => {
        setError(true);
        setUser(null);
      });
    } finally {
      startTransition(() => setLoading(false));
      fetchCalledRef.current = false;
    }
  }
  useEffect(() => {
    if (
      !localStorage.getItem("country") ||
      !localStorage.getItem("country_code") ||
      !localStorage.getItem("timezone")
    )
      getMyGeoLocation();
    else {
      startTransition(() => {
        setCountryCode(localStorage.getItem("country_code"));
        setISOCode(localStorage.getItem("country"));
        setTimeZone(localStorage.getItem("timezone"));
      });
    }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentUrl = new URL(window.location.href);
    const token = currentUrl.searchParams.get("token");

    if (token) {
      (async () => {
        let response = null;
        let exchangeSucceeded = false;

        try {
          const res = await fetch(
            process.env.NEXT_PUBLIC_API_URL + "/" + ENDPOINTS.ONE_TIME_LOGIN,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...DEV_AUTH_HEADERS,
              },
              credentials: "include",
              body: JSON.stringify({ token }),
            },
          );
          response = await res.json();
          exchangeSucceeded = res.ok && response?.status;
        } catch (error) {
          // Network failure — fall through and just strip the token below.
        }

        if (exchangeSucceeded) {
          const userData =
            response?.data?.customer ||
            response?.data?.user ||
            (response?.data?.id ? response?.data : null);

          if (userData) {
            setUser(userData);
          }
          setIsOpen(false);

          // Verify and hydrate full profile from /me
          await fetchUser();
          setIsOpen(false);

          // Remove token from URL, but PRESERVE all other query params (especially ?id=...)
          currentUrl.searchParams.delete("token");
          const cleanedSearch = currentUrl.searchParams.toString();
          const cleanedUrl = `${currentUrl.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}`;

          // If the user arrived on a dedicated auth entry route (/impersonate or /login),
          // forward them to the redirect path or home.
          const redirectPath = response?.data?.redirect_path;
          if (
            currentUrl.pathname === "/impersonate" ||
            currentUrl.pathname === "/login"
          ) {
            if (redirectPath) {
              router.replace(`/${redirectPath.replace(/^\//, "")}`);
            } else {
              router.replace("/");
            }
            return;
          }

          // For content routes (e.g. /tickets?id=...), stay on the page and clean the URL in history
          window.history.replaceState({}, "", cleanedUrl);
          return;
        }

        // Exchange failed (bad/expired token) — fall back to whatever session
        // the existing cookies describe (may be nothing → stays on login).
        await fetchUser();

        const redirectPath = response?.data?.redirect_path;
        if (
          redirectPath &&
          (currentUrl.pathname === "/impersonate" ||
            currentUrl.pathname === "/login")
        ) {
          router.replace(`/${redirectPath.replace(/^\//, "")}`);
          return;
        }

        if (currentUrl.pathname === "/impersonate") {
          router.replace("/");
          return;
        }

        currentUrl.searchParams.delete("token");
        const cleanedSearch = currentUrl.searchParams.toString();
        window.history.replaceState(
          {},
          "",
          `${currentUrl.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}`,
        );
      })();
    } else {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <usercontext.Provider
      value={{
        user,
        setUser,
        loading,
        error,
        isOpen,
        setIsOpen,
        countryCode,
        isoCode,
        timeZone,
        timeLoading,
        specialFn,
        setSpecialFn,
        receivedTicket,
        setReceivedTicket,
      }}
    >
      {children}
      <ReceivedTicketModal
        ticket={receivedTicket}
        open={Boolean(receivedTicket)}
        onClose={() => setReceivedTicket(null)}
      />
    </usercontext.Provider>
  );
}
